import { ReactNode, useEffect, useMemo } from 'react'
import { initialQueryState, OptionsHelper, useDebounce } from '_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from 'app/components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  actions?: ReactNode
}

export const UsuariosListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.nome.trim() ? `pessoa.nome~*${debouncedFilters.nome.trim()}*` : '',
        debouncedFilters.situacao ? `ativo:${debouncedFilters.situacao.value}` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.nome.trim() && {
      key: 'nome',
      label: t('nome.label'),
      value: `“${filters.nome.trim()}”`,
      onRemove: () => setFilter('nome', ''),
    },
    filters.situacao && {
      key: 'situacao',
      label: t('situacao.label'),
      value: filters.situacao.label,
      onRemove: () => setFilter('situacao', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'usuário encontrado' : 'usuários encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('nome.label')} wide>
        <Input
          entity='nome'
          value={filters.nome}
          onChange={(e) => setFilter('nome', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('situacao.label')}>
        <CustomSelect
          entity='situacao'
          onChange={(situacao?: IOption<boolean>) => setFilter('situacao', situacao ?? undefined)}
          options={OptionsHelper.statusCadastro}
          value={filters.situacao}
        />
      </TableFilter>
    </TableFilters>
  )
}
